import type { PersonOrgChart } from "@mirage/service-people/types";

export function getDirectReports(
  person: PersonOrgChart,
  allPeople: PersonOrgChart[],
): PersonOrgChart[] {
  return allPeople.filter((p) => p.managerUuid === person.uuid);
}

export function getPeers(
  person: PersonOrgChart,
  allPeople: PersonOrgChart[],
  includeSelf: boolean = false,
): PersonOrgChart[] {
  if (!person.managerUuid) {
    return [];
  }
  return allPeople.filter(
    (p) =>
      p.managerUuid === person.managerUuid &&
      (includeSelf || p.uuid !== person.uuid),
  );
}

export function getReportingLine(
  person: PersonOrgChart,
  allPeople: PersonOrgChart[],
): PersonOrgChart[] {
  const reportingLine: PersonOrgChart[] = [person];
  const allPeopleByUuid = new Map(allPeople.map((p) => [p.uuid, p]));
  let nextPerson: PersonOrgChart | undefined = person;
  while (nextPerson.managerUuid) {
    nextPerson = allPeopleByUuid.get(nextPerson.managerUuid);
    if (!nextPerson) {
      break; // No more managers found
    }
    reportingLine.push(nextPerson);
  }
  return reportingLine;
}

export function getBirthdays(allPeople: PersonOrgChart[]): PersonOrgChart[] {
  // Filter people with birthdays from today until Sunday, inclusive
  const datesToInclude: string[] = [];
  const day = new Date();

  // Add all the dates from today until the next Monday to datesToInclude
  // It's a little awkward, but faster than parsing all the birthday strings
  // and doing date comparison to start and end dates for the week. And the
  // code has less math to get wrong.
  do {
    const monthString =
      day.getUTCMonth() < 10
        ? `0${day.getUTCMonth() + 1}`
        : `${day.getUTCMonth() + 1}`;
    const dayString =
      day.getUTCDate() < 10 ? `0${day.getUTCDate()}` : `${day.getUTCDate()}`;
    datesToInclude.push(`${monthString}-${dayString}`);
    day.setUTCDate(day.getUTCDate() + 1);
  } while (day.getUTCDay() !== 1); // 1 is Monday

  return allPeople.filter((person) => {
    if (!person.birthday || person.isDeactivated) {
      return false;
    }
    return datesToInclude.includes(person.birthday);
  });
}

export function getWorkAnniversaries(
  allPeople: PersonOrgChart[],
): PersonOrgChart[] {
  return allPeople.filter((person) => {
    // not null and not 0
    // - null means we received nothing from the server (tenure_undefined was set to true)
    // - 0 means new hire
    if (person.isDeactivated) {
      return false;
    }
    return person.tenure && person.tenure % 12 === 0;
  });
}

export function getNewHires(allPeople: PersonOrgChart[]): PersonOrgChart[] {
  return allPeople.filter((person) => {
    if (person.isDeactivated) {
      return false;
    }
    return person.tenure === 0;
  });
}
